import React, { useState, useEffect, useCallback, memo } from 'react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Search, X, Plus, Star, Edit2, Trash2, Music, Heart, ExternalLink, Sparkles, AlertCircle, RefreshCw, TrendingUp, Download } from 'lucide-react';
import { toast } from 'sonner';
import { LibraryFilters } from './LibraryFilters';

import { useSongsContext } from '../contexts/SongsContext';
import { useSpotify } from '../SpotifyContext';
import axios from 'axios';
import { useQueryClient } from '@tanstack/react-query';
import { API_URL } from '../config';

const API = API_URL;

const ITEMS_PER_PAGE = 20;

const StarRating = memo(({ rating, onRate, editable = false }) => {
  return (
    <div className="flex gap-1">
      {[1, 2, 3, 4, 5].map((star) => (
        <Star
          key={star}
          className={`w-4 h-4 ${
            star <= rating ? "fill-amber-400 text-amber-400" : "text-gray-300"
          } ${editable ? "cursor-pointer hover:scale-110 transition-transform" : ""}`}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            if (editable) onRate(star === rating ? 0 : star);
          }}
        />
      ))}
    </div>
  );
});

export const SongLibrary = ({
  // Props that come from parent (App.js)
  user,
  isLiveMode,
  denonPendientesCount,
  fetchDenonPendientesCount,
  customGenres,
  setCustomGenres,
  getAllMomentos,
  MOMENTOS_BODA,
  onAddSong,
  onEditSong,
  onDeleteSong,
  onToggleFavorite,
  onToggleDenon,
  onDirectRatingChange,
  selectedSongs,
  setSelectedSongs,
  handleMultipleSelection,
  onOpenSpotifySearch,
  onDetectDuplicates,
  onAddStyle,
  onDeleteStyle,
  onAddMomento,
  onDeleteMomento,
  onAutoAssignStyle,
  onBulkAutoAssignStyle,
  onLinkAllSpotify,
  onOpenSmartPlaylists,
  onOpenPlaylistAnalyzer,
  onRelinkSongToSpotify,
}) => {
  const { accessToken: spotifyAccessToken } = useSpotify();
  
  const {
    searchText,
    setSearchText,
    filteredSongs,
    showFavoritesOnly,
    setShowFavoritesOnly,
    showOnlyDenonPendientes,
    setShowOnlyDenonPendientes,
    filterRating,
    setFilterRating,
    filterEnergia,
    setFilterEnergia,
    filterMomento,
    setFilterMomento,
    filterGenre,
    setFilterGenre,
    filterDecade,
    setFilterDecade,
    hasActiveFilters,
    clearAllFilters,
    availableGenres,
    // Sorting
    sortBy,
    setSortBy,
    sortOrder,
    setSortOrder,
    // Pagination from context (server-side)
    currentPage,
    setCurrentPage,
    totalPages,
    totalSongs,
    hasNextPage,
    hasPrevPage,
  } = useSongsContext();

  const queryClient = useQueryClient();
  
  // Force refresh songs when component mounts
  useEffect(() => {
    console.log('[SongLibrary] Component mounted, forcing refresh...');
    
    // Invalidate and refetch songs query immediately
    queryClient.invalidateQueries({ queryKey: ['songs'] });
  // eslint-disable-next-line react-hooks/exhaustive-deps -- deps: queryClient
  }, []); // Empty deps = only on mount

  // Estado para edición en bloque
  const [showBulkEditDialog, setShowBulkEditDialog] = useState(false);
  const [bulkEditAction, setBulkEditAction] = useState('add_genre');
  const [bulkEditValue, setBulkEditValue] = useState('');
  const [isBulkEditing, setIsBulkEditing] = useState(false);

  // Repasar pendientes de valorar: modo rápido para puntuar de golpe todas
  // las canciones "Sin valoración", sin tener que buscarlas una a una.
  const [showReviewPendingDialog, setShowReviewPendingDialog] = useState(false);
  const [pendingReviewSongs, setPendingReviewSongs] = useState([]);
  const [isLoadingPendingReview, setIsLoadingPendingReview] = useState(false);

  const handleOpenReviewPending = async () => {
    setShowReviewPendingDialog(true);
    setIsLoadingPendingReview(true);
    try {
      const authToken = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/songs`, {
        params: { rating: '__sin_valoracion__', limit: 500, sort_by: 'title', sort_order: 'asc' },
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setPendingReviewSongs(data.data || []);
    } catch (error) {
      console.error('Error al cargar canciones sin valorar:', error);
      toast.error('Error al cargar canciones pendientes de valorar');
      setShowReviewPendingDialog(false);
    } finally {
      setIsLoadingPendingReview(false);
    }
  };

  const handleRatePendingSong = async (song, newRating) => {
    if (!newRating) return; // ignora el "desmarcar" (misma estrella de nuevo)
    try {
      await onDirectRatingChange(song, newRating);
      setPendingReviewSongs((prev) => prev.filter((s) => s.id !== song.id));
    } catch (error) {
      console.error('Error al valorar canción:', error);
    }
  };

  const handleCloseReviewPending = () => {
    setShowReviewPendingDialog(false);
    queryClient.invalidateQueries({ queryKey: ['songs'] });
  };

  // Songs are already paginated from backend, no need to slice
  const paginatedSongs = filteredSongs;

  // Función para edición en bloque
  const handleBulkEdit = async () => {
    if (selectedSongs.length === 0 || !bulkEditValue) return;
    
    setIsBulkEditing(true);
    try {
      const authToken = localStorage.getItem('token');
      const response = await axios.post(`${API}/songs/bulk/edit`, {
        song_ids: selectedSongs,
        action: bulkEditAction,
        value: bulkEditAction === 'set_rating' ? parseInt(bulkEditValue) : 
               bulkEditAction === 'set_favorito' ? bulkEditValue === 'true' : 
               bulkEditValue
      }, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      if (response.data.success) {
        toast.success(`✅ ${response.data.message}`);
        setShowBulkEditDialog(false);
        setBulkEditValue('');
        setSelectedSongs([]);
        // Refrescar la lista
        queryClient.invalidateQueries(['songs']);
      }
    } catch (error) {
      console.error('Error en edición en bloque:', error);
      toast.error('Error al editar canciones');
    } finally {
      setIsBulkEditing(false);
    }
  };

  // Seleccionar TODOS los resultados filtrados (o deseleccionar si ya están todos)
  const [isLoadingAllIds, setIsLoadingAllIds] = useState(false);

  // Iter91: Importación masiva desde URL playlist Spotify
  const [isImportingSpotify, setIsImportingSpotify] = useState(false);
  const handleImportFromSpotify = async () => {
    if (isImportingSpotify) return;
    const url = window.prompt(
      'Pega la URL de la playlist Spotify a importar:\n\n' +
      'Ej: https://open.spotify.com/playlist/37i9dQZF1DX...\n\n' +
      'Las canciones que ya estén en biblioteca se saltarán automáticamente.\n' +
      'Las nuevas se añadirán con género, momento, rating y energía sugeridos por IA.'
    );
    if (!url || !url.trim()) return;
    const label = window.prompt('Etiqueta opcional (ej: "Boda Junio 2026"):', '') || null;

    setIsImportingSpotify(true);
    const toastId = toast.loading('Importando playlist Spotify... esto puede tardar 1-2 minutos para playlists grandes', { duration: 120000 });
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/songs/import-from-spotify-playlist`,
        {
          url: url.trim(),
          label: label?.trim() || null,
          auto_enrich: true,
          custom_genres: customGenres || [],
          pendiente_denon: false,
        },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 }
      );
      toast.dismiss(toastId);
      const msg = `✅ Importadas ${data.tracks_añadidas} canciones nuevas` +
        (data.tracks_ya_existentes > 0 ? ` · ${data.tracks_ya_existentes} ya existían` : '') +
        (data.tracks_fallidas > 0 ? ` · ${data.tracks_fallidas} fallidas` : '');
      toast.success(msg, { duration: 8000 });
      // Refrescar listado
      try { await queryClient.invalidateQueries({ queryKey: ['songs'] }); } catch {}
    } catch (e) {
      toast.dismiss(toastId);
      const detail = e?.response?.data?.detail || e?.message || 'Error desconocido';
      toast.error(`Error importando: ${detail}`, { duration: 6000 });
    } finally {
      setIsImportingSpotify(false);
    }
  };
  
  // Analizar BPM faltantes (MusicBrainz/AcousticBrainz, lotes)
  const [bpmStats, setBpmStats] = useState(null);
  const [isAnalyzingBpm, setIsAnalyzingBpm] = useState(false);
  
  const loadBpmStats = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.get(`${API}/songs/bpm/stats`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBpmStats(data);
    } catch (e) {
      // silent — stats are optional
    }
  }, []);
  
  useEffect(() => {
    if (user?.role === 'admin') loadBpmStats();
  }, [user, loadBpmStats]);
  
  const handleAnalyzeMissingBpm = async () => {
    if (isAnalyzingBpm) return;
    const pendientes = bpmStats?.pendientes_analizar || 0;
    if (pendientes === 0) {
      toast.info('No hay canciones pendientes de analizar');
      return;
    }
    const limit = Math.min(20, pendientes);
    const etaSec = Math.ceil(limit * 3);  // ~3s por canción (MusicBrainz rate limit)
    
    const ok = window.confirm(
      `📊 Analizar BPM faltantes (MusicBrainz + AcousticBrainz)\n\n` +
      `• ${pendientes} canciones sin BPM\n` +
      `• Se procesará un lote de ${limit}\n` +
      `• Tiempo estimado: ~${etaSec}s\n` +
      `• Hit rate típico: 30-50% (AcousticBrainz está "frozen" desde 2022)\n\n` +
      `¿Continuar?`
    );
    if (!ok) return;
    
    setIsAnalyzingBpm(true);
    const loadingToast = toast.loading(`Analizando ${limit} canciones... (~${etaSec}s)`);
    try {
      const token = localStorage.getItem('token');
      const { data } = await axios.post(
        `${API}/songs/bpm/analyze-missing`,
        { limit, skip_processed: true },
        { headers: { Authorization: `Bearer ${token}` }, timeout: 180000 },
      );
      toast.dismiss(loadingToast);
      toast.success(
        `✅ ${data.found}/${data.processed} BPM encontrados. Quedan ${data.remaining} pendientes.`,
        { duration: 6000 },
      );
      // Refrescar stats + cache de canciones
      await loadBpmStats();
      queryClient.invalidateQueries({ queryKey: ['songs'] });
    } catch (e) {
      toast.dismiss(loadingToast);
      toast.error(`Error analizando BPM: ${e.response?.data?.detail || e.message}`);
    } finally {
      setIsAnalyzingBpm(false);
    }
  };
  
  const handleSelectAllFiltered = async () => {
    // Si ya están todos seleccionados, deseleccionar
    if (selectedSongs.length === totalSongs && totalSongs > 0) {
      setSelectedSongs([]);
      return;
    }
    
    setIsLoadingAllIds(true);
    try {
      const authToken = localStorage.getItem('token');
      const params = new URLSearchParams();
      
      if (searchText) params.append('search', searchText);
      // Solo enviar filtros si no son 'all' o vacíos
      // IMPORTANTE: Usar los mismos nombres de parámetros que /api/songs
      if (filterGenre && filterGenre !== 'all') params.append('genre', filterGenre);
      if (filterMomento && filterMomento !== 'all') params.append('momento', filterMomento);
      if (showFavoritesOnly) params.append('favorito', 'true');  // Cambiado de 'favoritos' a 'favorito'
      if (filterRating && filterRating !== 'all') {
        params.append('rating', filterRating);  // Puede ser un número o '__sin_valoracion__'
      }
      if (filterDecade && filterDecade !== 'all') params.append('decade', filterDecade);
      if (showOnlyDenonPendientes) params.append('pendiente_denon', 'true');  // Añadido soporte
      
      console.log('[SelectAll] Params:', params.toString());
      
      const response = await axios.get(`${API}/songs/filtered-ids?${params.toString()}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      
      console.log('[SelectAll] Response:', response.data);
      
      if (response.data.ids && response.data.ids.length > 0) {
        setSelectedSongs(response.data.ids);
        toast.success(`✅ ${response.data.count} canciones seleccionadas`);
      } else {
        toast.info('No se encontraron canciones con los filtros actuales');
      }
    } catch (error) {
      console.error('Error al obtener todos los IDs:', error);
      toast.error('Error al seleccionar todas las canciones');
    } finally {
      setIsLoadingAllIds(false);
    }
  };

  const handleSelectAll = () => {
    if (selectedSongs.length === filteredSongs.length) {
      setSelectedSongs([]);
    } else {
      setSelectedSongs(filteredSongs.map(s => s.id));
    }
  };

  const handleDeleteSelected = async () => {
    if (selectedSongs.length === 0) return;

    const confirmMessage = `¿Estás seguro de que quieres eliminar ${selectedSongs.length} canción${selectedSongs.length !== 1 ? 'es' : ''} seleccionada${selectedSongs.length !== 1 ? 's' : ''}?`;
    
    if (!window.confirm(confirmMessage)) return;

    try {
      toast.info(`🗑️ Eliminando ${selectedSongs.length} canciones...`);
      
      let deleted = 0;
      let failed = 0;
      
      // Delete each selected song WITHOUT individual confirmations
      for (const songId of selectedSongs) {
        try {
          await onDeleteSong(songId, true); // true = skip confirmation
          deleted++;
        } catch (error) {
          failed++;
        }
      }
      
      if (deleted > 0) {
        toast.success(`✅ ${deleted} canción${deleted !== 1 ? 'es' : ''} eliminada${deleted !== 1 ? 's' : ''}`);
      }
      
      if (failed > 0) {
        toast.error(`❌ ${failed} canciones no se pudieron eliminar`);
      }
      
      setSelectedSongs([]);
    } catch (error) {
      toast.error('Error al eliminar canciones');
    }
  };

  const handleDeleteFiltered = async () => {
    if (filteredSongs.length === 0) return;

    const filterDescription = [];
    if (searchText) filterDescription.push(`búsqueda: "${searchText}"`);
    if (filterGenre && filterGenre !== 'all') filterDescription.push(`género: ${filterGenre}`);
    if (filterRating && filterRating !== 'all') {
      filterDescription.push(filterRating === '__sin_valoracion__' ? 'sin valoración' : `valoración: ${filterRating}★`);
    }
    if (filterMomento && filterMomento !== 'all') filterDescription.push(`momento: ${filterMomento}`);
    if (filterEnergia && filterEnergia !== 'all') filterDescription.push(`energía: ${filterEnergia}`);
    if (showFavoritesOnly) filterDescription.push('favoritos');
    if (showOnlyDenonPendientes) filterDescription.push('pendientes Denon');

    const description = filterDescription.length > 0 
      ? ` con filtros: ${filterDescription.join(', ')}` 
      : '';

    const confirmMessage = `⚠️ ATENCIÓN: Vas a eliminar ${filteredSongs.length} canción${filteredSongs.length !== 1 ? 'es' : ''}${description}.\n\n¿Estás completamente seguro? Esta acción NO se puede deshacer.`;
    
    if (!window.confirm(confirmMessage)) return;

    // Double confirmation for safety
    const doubleConfirm = window.confirm(`ÚLTIMA CONFIRMACIÓN: ¿Eliminar definitivamente ${filteredSongs.length} canciones?`);
    if (!doubleConfirm) return;

    try {
      const songIds = filteredSongs.map(s => s.id);
      
      toast.info(`🗑️ Eliminando ${songIds.length} canciones...`);
      
      let deleted = 0;
      let failed = 0;
      
      // Delete each song WITHOUT individual confirmations
      for (const songId of songIds) {
        try {
          await onDeleteSong(songId, true); // true = skip confirmation
          deleted++;
          
          // Update progress every 10 songs
          if (deleted % 10 === 0) {
            toast.info(`🗑️ Progreso: ${deleted}/${songIds.length} eliminadas...`);
          }
        } catch (error) {
          failed++;
        }
      }
      
      if (deleted > 0) {
        toast.success(`✅ ${deleted} canción${deleted !== 1 ? 'es' : ''} eliminada${deleted !== 1 ? 's' : ''} correctamente`);
      }
      
      if (failed > 0) {
        toast.error(`❌ ${failed} canciones no se pudieron eliminar`);
      }
      
      setSelectedSongs([]);
    } catch (error) {
      toast.error('Error al eliminar canciones filtradas');
    }
  };

  const handleDeleteAllLibrary = async () => {
    // Get the actual total from context
    const libraryTotal = totalSongs || filteredSongs.length;

    const confirmMessage = `🚨 ¡PELIGRO! Vas a ELIMINAR TODA LA BIBLIOTECA (${libraryTotal} canciones).\n\nEsta acción es IRREVERSIBLE y borrará TODAS las canciones de todos los géneros y filtros.\n\n¿Estás ABSOLUTAMENTE seguro?`;
    
    if (!window.confirm(confirmMessage)) return;

    // Multiple confirmations for such a destructive action
    const confirm2 = window.confirm(`Segunda confirmación: ¿Eliminar TODAS las ${libraryTotal} canciones de la biblioteca completa?`);
    if (!confirm2) return;

    const confirm3 = window.prompt(`ÚLTIMA CONFIRMACIÓN: Escribe "BORRAR TODO" (en mayúsculas) para confirmar la eliminación completa de la biblioteca:`);
    if (confirm3 !== 'BORRAR TODO') {
      toast.info('Cancelado. La biblioteca está a salvo.');
      return;
    }

    try {
      toast.info(`🗑️ Eliminando TODA la biblioteca (${libraryTotal} canciones)...`);
      
      // Call backend endpoint to delete ALL songs
      const response = await axios.delete(`${API}/songs/bulk/all`);
      
      const deletedCount = response.data.deleted_count;
      
      // Show final result
      toast.success(`✅ Biblioteca completamente eliminada (${deletedCount} canciones borradas)`, {
        duration: 5000
      });
      
      // Trigger refresh of songs list
      queryClient.invalidateQueries(['songs']);
      
    } catch (error) {
      console.error('Error deleting all songs:', error);
      const errorMsg = error.response?.data?.detail || 'Error al eliminar la biblioteca';
      toast.error(`Error: ${errorMsg}`);
    }
  };

  const handleEnrichSingleSong = async (song) => {
    if (!song.spotify_url) {
      toast.error('Esta canción no tiene enlace de Spotify. Primero vincúlala con Spotify.');
      return;
    }

    try {
      toast.info(`🎵 Obteniendo información de Spotify para "${song.title}"...`);
      
      const response = await axios.post(
        `${API}/songs/${song.id}/enrich-from-spotify`,
        {
          access_token: spotifyAccessToken || null
        },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );

      if (response.data.success) {
        const fieldsUpdated = response.data.fields_updated || [];
        if (fieldsUpdated.length > 0) {
          toast.success(`✅ ${response.data.message}\n${fieldsUpdated.join(', ')}`);
        } else {
          toast.info('ℹ️ No se encontró información nueva para esta canción');
        }
        
        // Refresh songs list
        queryClient.invalidateQueries(['songs']);
      } else {
        toast.warning(response.data.message || 'No se pudo obtener información de Spotify');
      }
    } catch (error) {
      console.error('Error enriching song:', error);
      const errorMsg = error.response?.data?.detail || 'Error al obtener información de Spotify';
      toast.error(errorMsg);
    }
  };

  return (
    <div className="space-y-4">
      {/* Search Bar */}
      <div className="mb-4">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-slate-400 w-5 h-5" />
          <Input
            type="text"
            placeholder="Buscar por título, artista o BPM..."
            className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white text-base h-12"
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
          />
          {searchText && (
            <button
              onClick={() => setSearchText("")}
              className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>
      </div>

      {/* Quick Filters */}
      <LibraryFilters
        showFavoritesOnly={showFavoritesOnly}
        setShowFavoritesOnly={setShowFavoritesOnly}
        showOnlyDenonPendientes={showOnlyDenonPendientes}
        setShowOnlyDenonPendientes={setShowOnlyDenonPendientes}
        denonPendientesCount={denonPendientesCount}
        filterRating={filterRating}
        setFilterRating={setFilterRating}
        filterEnergia={filterEnergia}
        setFilterEnergia={setFilterEnergia}
        filterMomento={filterMomento}
        setFilterMomento={setFilterMomento}
        filterGenre={filterGenre}
        setFilterGenre={setFilterGenre}
        filterDecade={filterDecade}
        setFilterDecade={setFilterDecade}
        hasActiveFilters={hasActiveFilters}
        clearAllFilters={clearAllFilters}
        fetchDenonPendientesCount={fetchDenonPendientesCount}
        sortBy={sortBy}
        setSortBy={setSortBy}
        sortOrder={sortOrder}
        setSortOrder={setSortOrder}
        MOMENTOS_BODA={MOMENTOS_BODA}
        availableGenres={availableGenres}
      />

      {/* Action Buttons */}
      <div className="mb-4 space-y-2">
        {/* Multiple selection controls */}
        {selectedSongs.length > 0 && (
          <div className="bg-purple-900/30 border border-purple-600 rounded-lg p-3 flex flex-wrap items-center gap-2">
            <span className="text-purple-200 text-sm font-medium">
              {selectedSongs.length} seleccionada{selectedSongs.length !== 1 ? 's' : ''}
            </span>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setSelectedSongs([])}
              className="bg-slate-700 border-slate-600 hover:bg-slate-600 text-xs"
            >
              Limpiar
            </Button>
            {user?.role === 'admin' && (
              <>
                <Button
                  size="sm"
                  onClick={() => setShowBulkEditDialog(true)}
                  className="bg-cyan-600 hover:bg-cyan-700 text-xs"
                >
                  <Edit2 className="w-3 h-3 mr-1" />
                  Editar en Bloque ({selectedSongs.length})
                </Button>
                <Button
                  size="sm"
                  onClick={handleMultipleSelection}
                  className="bg-purple-600 hover:bg-purple-700 text-xs"
                >
                  <Music className="w-3 h-3 mr-1" />
                  Enviar a Cola Spotify ({selectedSongs.length})
                </Button>
                <Button
                  size="sm"
                  onClick={handleDeleteSelected}
                  className="bg-red-600 hover:bg-red-700 text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Borrar Seleccionadas ({selectedSongs.length})
                </Button>
              </>
            )}
          </div>
        )}

        {/* Main action buttons row */}
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-2">
          {!isLiveMode && (
            <Button
              onClick={onAddSong}
              className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm h-9"
              data-testid="library-add-song-btn"
            >
              <Plus className="w-4 h-4 sm:mr-2" />
              <span className="hidden sm:inline">+ Canción</span>
            </Button>
          )}

          <Button
            onClick={onOpenSpotifySearch}
            className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm h-9"
          >
            <Music className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Spotify</span>
          </Button>

          {user?.role === 'admin' && (
            <Button
              onClick={handleImportFromSpotify}
              disabled={isImportingSpotify}
              className="bg-emerald-600 hover:bg-emerald-700 text-xs sm:text-sm h-9"
              data-testid="import-from-spotify-playlist-btn"
              title="Importar todas las canciones de una playlist Spotify (con género/momento/rating sugeridos por IA)"
            >
              {isImportingSpotify ? (
                <RefreshCw className="w-4 h-4 sm:mr-1 animate-spin" />
              ) : (
                <Download className="w-4 h-4 sm:mr-1" />
              )}
              <span className="hidden sm:inline">
                {isImportingSpotify ? 'Importando...' : 'Importar Playlist'}
              </span>
            </Button>
          )}

          {/* Seleccionar TODOS los resultados filtrados */}
          <Button
            onClick={handleSelectAllFiltered}
            disabled={isLoadingAllIds || totalSongs === 0}
            variant="outline"
            className={`text-xs sm:text-sm h-9 ${
              selectedSongs.length > 0 && selectedSongs.length === totalSongs
                ? 'bg-purple-600 border-purple-500 text-white'
                : 'bg-slate-700 border-slate-600 hover:bg-slate-600'
            }`}
            title={`Seleccionar las ${totalSongs} canciones`}
          >
            {isLoadingAllIds ? (
              <RefreshCw className="w-3 h-3 mr-1 animate-spin" />
            ) : (
              <input
                type="checkbox"
                checked={selectedSongs.length > 0 && selectedSongs.length === totalSongs}
                readOnly
                className="mr-2"
              />
            )}
            <span className="hidden sm:inline">Todos ({totalSongs})</span>
            <span className="sm:hidden">{totalSongs}</span>
          </Button>

          {user?.role === 'admin' && (
            <>
              <Button
                onClick={onAddStyle}
                variant="outline"
                className="bg-slate-700 border-slate-600 hover:bg-slate-600 text-xs sm:text-sm h-9"
              >
                <Plus className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Estilo</span>
              </Button>

              <Button
                onClick={onDeleteStyle}
                variant="outline"
                className="bg-red-900/30 border-red-600 hover:bg-red-900/50 text-red-400 text-xs sm:text-sm h-9"
              >
                <Trash2 className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Borrar Estilo</span>
              </Button>

              <Button
                onClick={onDetectDuplicates}
                variant="outline"
                className="bg-orange-900/30 border-orange-600 hover:bg-orange-900/50 text-orange-400 text-xs sm:text-sm h-9"
              >
                <AlertCircle className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">Gestionar Duplicados</span>
              </Button>

              <Button
                onClick={onOpenSmartPlaylists}
                className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-xs sm:text-sm h-9"
              >
                <Sparkles className="w-3 h-3 sm:mr-1" />
                <span className="hidden sm:inline">✨ Playlists Inteligentes</span>
              </Button>

              {!isLiveMode && (
                <>
                  <Button
                    onClick={onOpenPlaylistAnalyzer}
                    className="bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-xs sm:text-sm h-9"
                    data-testid="open-playlist-analyzer-btn"
                  >
                    <TrendingUp className="w-3 h-3 sm:mr-1" />
                    <span className="hidden sm:inline">📊 Análisis Playlists</span>
                  </Button>

                  <Button
                    onClick={handleAnalyzeMissingBpm}
                    disabled={isAnalyzingBpm}
                    className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-xs sm:text-sm h-9"
                    title={bpmStats
                      ? `BPM: ${bpmStats.con_bpm}/${bpmStats.total} (${bpmStats.cobertura_pct}%) · ${bpmStats.pendientes_analizar} pendientes`
                      : 'Analizar BPM faltantes con MusicBrainz/AcousticBrainz'}
                    data-testid="analyze-missing-bpm-btn"
                  >
                    {isAnalyzingBpm ? (
                      <RefreshCw className="w-3 h-3 sm:mr-1 animate-spin" />
                    ) : (
                      <span className="mr-1">📊</span>
                    )}
                    <span className="hidden sm:inline">
                      Analizar BPM
                      {bpmStats?.pendientes_analizar > 0 && (
                        <span className="ml-1 text-emerald-200">
                          ({bpmStats.pendientes_analizar})
                        </span>
                      )}
                    </span>
                  </Button>

                  <Button
                    onClick={onLinkAllSpotify}
                    className="bg-green-600 hover:bg-green-700 text-xs sm:text-sm h-9"
                    title="Vincular todas las canciones sin enlace con Spotify"
                  >
                    <ExternalLink className="w-3 h-3 sm:mr-1" />
                    <span className="hidden sm:inline">🔗 Vincular Spotify</span>
                  </Button>

                </>
              )}

            </>
          )}

          {/* Botón de auto-asignar estilos masivo - Disponible para todos */}
          {onBulkAutoAssignStyle && (
            <Button
              onClick={() => onBulkAutoAssignStyle(true)}
              className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-xs sm:text-sm h-9"
              title="Auto-asignar estilos a todas las canciones sin estilo usando IA"
            >
              🎨 <span className="hidden sm:inline ml-1">Auto-Estilos</span>
            </Button>
          )}

          {/* Repasar canciones sin valorar - Disponible para todos */}
          <Button
            onClick={handleOpenReviewPending}
            className="bg-gradient-to-r from-amber-600 to-orange-600 hover:from-amber-700 hover:to-orange-700 text-xs sm:text-sm h-9"
            title="Repasar rápido todas las canciones sin valoración, una por una"
            data-testid="review-pending-ratings-btn"
          >
            ⭐ <span className="hidden sm:inline ml-1">Repasar pendientes</span>
          </Button>
        </div>

        {/* Bulk Delete Actions (Admin only) - Oculto en Modo En Vivo */}
        {user?.role === 'admin' && !isLiveMode && (
          <div className="bg-red-900/20 border border-red-600/50 rounded-lg p-3">
            <div className="flex flex-col sm:flex-row gap-2">
              <div className="flex-1">
                <p className="text-red-200 text-xs sm:text-sm font-medium mb-1">🗑️ Borrado Masivo</p>
                <p className="text-red-300/70 text-[10px] sm:text-xs">Elimina múltiples canciones a la vez</p>
              </div>
              <div className="flex gap-2 flex-wrap">
                {hasActiveFilters && filteredSongs.length > 0 && (
                  <Button
                    size="sm"
                    onClick={handleDeleteFiltered}
                    className="bg-red-700 hover:bg-red-800 text-white text-xs"
                  >
                    <Trash2 className="w-3 h-3 mr-1" />
                    Borrar Filtradas ({filteredSongs.length})
                  </Button>
                )}
                <Button
                  size="sm"
                  onClick={handleDeleteAllLibrary}
                  variant="outline"
                  className="bg-red-900/50 border-red-600 hover:bg-red-900 text-red-200 text-xs"
                >
                  <Trash2 className="w-3 h-3 mr-1" />
                  Borrar Biblioteca Completa
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Songs Count */}
      <div className="text-slate-400 text-sm mb-2">
        {totalSongs} canción{totalSongs !== 1 ? 'es' : ''} encontrada{totalSongs !== 1 ? 's' : ''} - Página {currentPage} de {totalPages}
      </div>

      {/* Songs List */}
      <div className="grid gap-2 sm:gap-3">
        {paginatedSongs.map((song) => (
          <Card key={song.id} className="bg-slate-800 border-slate-700 hover:border-slate-600 transition-colors overflow-hidden">
            <CardContent className="p-2 sm:p-4">
              <div className="flex items-start gap-2">
                {/* Checkbox for multiple selection */}
                <input
                  type="checkbox"
                  checked={selectedSongs.includes(song.id)}
                  onChange={(e) => {
                    if (e.target.checked) {
                      setSelectedSongs([...selectedSongs, song.id]);
                    } else {
                      setSelectedSongs(selectedSongs.filter(id => id !== song.id));
                    }
                  }}
                  className="mt-1 w-3 h-3 sm:w-4 sm:h-4 cursor-pointer flex-shrink-0"
                />

                {/* Song Info */}
                <div className="flex-1 min-w-0 overflow-hidden">
                  <div className="flex items-start justify-between gap-1 sm:gap-2 mb-1 sm:mb-2">
                    <div className="flex-1 min-w-0 overflow-hidden">
                      <h3 className="font-semibold text-white text-xs sm:text-base truncate leading-tight">
                        {song.title}
                      </h3>
                      <p className="text-slate-400 text-xs truncate leading-tight">
                        {song.artist}
                      </p>
                    </div>

                    {/* Action Buttons */}
                    <div className="flex gap-0.5 sm:gap-1 flex-shrink-0">
                      {song.spotify_url && (
                        <a
                          href={song.spotify_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="p-1 sm:p-1.5 rounded hover:bg-slate-700 text-green-400"
                          title="Abrir en Spotify"
                        >
                          <ExternalLink className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </a>
                      )}
                      
                      {/* Re-link to Spotify button */}
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onRelinkSongToSpotify(song);
                        }}
                        className="p-1 sm:p-1.5 rounded hover:bg-slate-700 text-yellow-400 hover:text-yellow-300"
                        title="Re-vincular con Spotify (si el enlace es incorrecto)"
                      >
                        <RefreshCw className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>

                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          onToggleFavorite(song);
                        }}
                        className="p-1 sm:p-1.5 rounded hover:bg-slate-700"
                      >
                        <Heart
                          className={`w-3.5 h-3.5 sm:w-4 sm:h-4 ${
                            song.favorito ? "fill-pink-500 text-pink-500" : "text-slate-400"
                          }`}
                        />
                      </button>

                      <button
                        onClick={() => onEditSong(song)}
                        className="p-1 sm:p-1.5 rounded hover:bg-slate-700 text-cyan-400"
                      >
                        <Edit2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                      </button>

                      {user?.role === 'admin' && (
                        <button
                          onClick={() => onDeleteSong(song.id)}
                          className="p-1 sm:p-1.5 rounded hover:bg-slate-700 text-red-400"
                        >
                          <Trash2 className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Song Details */}
                  <div className="flex flex-wrap gap-1 sm:gap-2 text-xs">
                    {/* Genres - Limit to 2 on mobile */}
                    {song.genres && song.genres.slice(0, 2).map((genre, idx) => (
                      <span
                        key={idx}
                        className="px-1.5 py-0.5 sm:px-2 bg-blue-900/30 text-blue-300 rounded text-[10px] sm:text-xs whitespace-nowrap"
                      >
                        {genre}
                      </span>
                    ))}
                    {song.genres && song.genres.length > 2 && (
                      <span className="px-1.5 py-0.5 sm:px-2 bg-blue-900/30 text-blue-300 rounded text-[10px] sm:text-xs">
                        +{song.genres.length - 2}
                      </span>
                    )}
                    
                    {/* Auto-assign style button - shown when song has no genres (works with Spotify or AI) */}
                    {(!song.genres || song.genres.length === 0) && onAutoAssignStyle && (
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          onAutoAssignStyle(song, spotifyAccessToken, customGenres);
                        }}
                        className="px-1.5 py-0.5 sm:px-2 bg-gradient-to-r from-purple-600/50 to-pink-600/50 hover:from-purple-600 hover:to-pink-600 text-purple-200 rounded text-[10px] sm:text-xs whitespace-nowrap transition-all cursor-pointer flex items-center gap-1"
                        title={song.spotify_url ? "Auto-asignar estilo (Spotify + IA)" : "Auto-asignar estilo con IA"}
                      >
                        🎨 <span className="hidden sm:inline">Auto-estilo</span>
                      </button>
                    )}

                    {/* Momentos - Limit to 1 on mobile */}
                    {song.momentos && song.momentos.slice(0, 1).map((momento, idx) => (
                      <span
                        key={idx}
                        className="px-1.5 py-0.5 sm:px-2 bg-purple-900/30 text-purple-300 rounded text-[10px] sm:text-xs whitespace-nowrap"
                      >
                        {momento}
                      </span>
                    ))}
                    {song.momentos && song.momentos.length > 1 && (
                      <span className="px-1.5 py-0.5 sm:px-2 bg-purple-900/30 text-purple-300 rounded text-[10px] sm:text-xs">
                        +{song.momentos.length - 1}
                      </span>
                    )}

                    {/* BPM */}
                    {song.bpm && (
                      <span className="px-1.5 py-0.5 sm:px-2 bg-orange-900/30 text-orange-300 rounded text-[10px] sm:text-xs whitespace-nowrap">
                        {song.bpm}
                      </span>
                    )}

                    {/* Year */}
                    {song.year && (
                      <span className="px-1.5 py-0.5 sm:px-2 bg-slate-700/50 text-slate-300 rounded text-[10px] sm:text-xs whitespace-nowrap">
                        📅 {song.year}
                      </span>
                    )}

                    {/* Energy */}
                    {song.energia && (
                      <span
                        className={`px-1.5 py-0.5 sm:px-2 rounded text-[10px] sm:text-xs whitespace-nowrap ${
                          song.energia === "Muy Alta"
                            ? "bg-red-900/30 text-red-300"
                            : song.energia === "Alta"
                            ? "bg-orange-900/30 text-orange-300"
                            : song.energia === "Media"
                            ? "bg-yellow-900/30 text-yellow-300"
                            : "bg-blue-900/30 text-blue-300"
                        }`}
                      >
                        {song.energia === "Muy Alta" && "⚡⚡⚡⚡"}
                        {song.energia === "Alta" && "⚡⚡⚡"}
                        {song.energia === "Media" && "⚡⚡"}
                        {song.energia === "Baja" && "⚡"}
                        <span className="hidden sm:inline">{" " + song.energia}</span>
                      </span>
                    )}

                    {/* Denon Pendiente */}
                    {user?.role === 'admin' && (
                      <button
                        onClick={() => onToggleDenon(song)}
                        className={`px-1.5 py-0.5 sm:px-2 rounded cursor-pointer transition-colors text-[10px] sm:text-xs whitespace-nowrap ${
                          song.pendiente_denon
                            ? "bg-orange-600 text-white"
                            : "bg-slate-700 text-slate-400 hover:bg-slate-600"
                        }`}
                      >
                        🎧 <span className="hidden sm:inline">{song.pendiente_denon ? "Pendiente" : "Denon"}</span>
                      </button>
                    )}
                  </div>

                  {/* Rating */}
                  <div className="mt-1 sm:mt-2 flex items-center gap-2 scale-90 sm:scale-100 origin-left">
                    <StarRating
                      rating={song.rating || 0}
                      onRate={(newRating) => onDirectRatingChange(song, newRating)}
                      editable={true}
                    />
                  </div>

                  {/* Notes */}
                  {song.notas && (
                    <p className="mt-1 sm:mt-2 text-slate-400 text-[10px] sm:text-xs italic line-clamp-2">
                      {song.notas}
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex justify-center gap-2 mt-4">
          <Button
            onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
            disabled={currentPage === 1}
            variant="outline"
            size="sm"
            className="bg-slate-700 border-slate-600"
          >
            Anterior
          </Button>
          <span className="flex items-center px-4 text-slate-300">
            Página {currentPage} de {totalPages}
          </span>
          <Button
            onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
            disabled={currentPage === totalPages}
            variant="outline"
            size="sm"
            className="bg-slate-700 border-slate-600"
          >
            Siguiente
          </Button>
        </div>
      )}

      {/* Empty State */}
      {filteredSongs.length === 0 && (
        <div className="text-center py-12">
          <Music className="w-16 h-16 mx-auto text-slate-600 mb-4" />
          <p className="text-slate-400 text-lg mb-2">No hay canciones</p>
          <p className="text-slate-500 text-sm">
            {hasActiveFilters
              ? "Intenta ajustar los filtros"
              : "Añade tu primera canción para comenzar"}
          </p>
        </div>
      )}

      {/* Bulk Edit Dialog */}
      <Dialog open={showBulkEditDialog} onOpenChange={setShowBulkEditDialog}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white">
              Editar {selectedSongs.length} canciones
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4">
            {/* Acción */}
            <div>
              <label className="block text-sm text-slate-300 mb-2">Acción</label>
              <select
                value={bulkEditAction}
                onChange={(e) => {
                  setBulkEditAction(e.target.value);
                  setBulkEditValue('');
                }}
                className="w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2"
              >
                <option value="add_genre">➕ Añadir estilo</option>
                <option value="remove_genre">➖ Quitar estilo</option>
                <option value="set_rating">⭐ Establecer valoración</option>
                <option value="set_favorito">❤️ Marcar favorito</option>
                <option value="add_momento">🎵 Añadir momento</option>
                <option value="remove_momento">🎵 Quitar momento</option>
              </select>
            </div>
            
            {/* Valor según la acción */}
            <div>
              <label className="block text-sm text-slate-300 mb-2">
                {bulkEditAction.includes('genre') ? 'Estilo' : 
                 bulkEditAction.includes('rating') ? 'Valoración' :
                 bulkEditAction.includes('favorito') ? 'Estado' :
                 bulkEditAction.includes('momento') ? 'Momento' : 'Valor'}
              </label>
              
              {bulkEditAction === 'add_genre' || bulkEditAction === 'remove_genre' ? (
                <select
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2"
                >
                  <option value="">Seleccionar estilo...</option>
                  {availableGenres.map(genre => (
                    <option key={genre} value={genre}>{genre}</option>
                  ))}
                </select>
              ) : bulkEditAction === 'set_rating' ? (
                <select
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2"
                >
                  <option value="">Seleccionar valoración...</option>
                  <option value="5">⭐⭐⭐⭐⭐ (5)</option>
                  <option value="4">⭐⭐⭐⭐ (4)</option>
                  <option value="3">⭐⭐⭐ (3)</option>
                  <option value="2">⭐⭐ (2)</option>
                  <option value="1">⭐ (1)</option>
                  <option value="0">Sin valoración</option>
                </select>
              ) : bulkEditAction === 'set_favorito' ? (
                <select
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2"
                >
                  <option value="">Seleccionar...</option>
                  <option value="true">❤️ Marcar como favorito</option>
                  <option value="false">🤍 Quitar de favoritos</option>
                </select>
              ) : bulkEditAction === 'add_momento' || bulkEditAction === 'remove_momento' ? (
                <select
                  value={bulkEditValue}
                  onChange={(e) => setBulkEditValue(e.target.value)}
                  className="w-full bg-slate-900 border border-slate-700 text-white rounded-md px-3 py-2"
                >
                  <option value="">Seleccionar momento...</option>
                  {getAllMomentos && getAllMomentos().map(momento => (
                    <option key={momento} value={momento}>{momento}</option>
                  ))}
                </select>
              ) : null}
            </div>
            
            {/* Botones */}
            <div className="flex justify-end gap-2 pt-4">
              <Button
                variant="outline"
                onClick={() => {
                  setShowBulkEditDialog(false);
                  setBulkEditValue('');
                }}
                className="border-slate-600"
              >
                Cancelar
              </Button>
              <Button
                onClick={handleBulkEdit}
                disabled={!bulkEditValue || isBulkEditing}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {isBulkEditing ? 'Aplicando...' : `Aplicar a ${selectedSongs.length} canciones`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Repasar pendientes de valorar */}
      <Dialog open={showReviewPendingDialog} onOpenChange={(open) => { if (!open) handleCloseReviewPending(); }}>
        <DialogContent className="bg-slate-800 border-slate-700 text-white max-w-lg" data-testid="review-pending-dialog">
          <DialogHeader>
            <DialogTitle className="text-white flex items-center gap-2">
              ⭐ Repasar pendientes de valorar
              {!isLoadingPendingReview && (
                <span className="text-sm font-normal text-slate-400">
                  ({pendingReviewSongs.length} {pendingReviewSongs.length === 1 ? 'canción' : 'canciones'})
                </span>
              )}
            </DialogTitle>
          </DialogHeader>

          {isLoadingPendingReview ? (
            <div className="py-10 text-center text-slate-400">
              <RefreshCw className="w-6 h-6 animate-spin mx-auto mb-2" />
              Cargando canciones sin valorar...
            </div>
          ) : pendingReviewSongs.length === 0 ? (
            <div className="py-10 text-center" data-testid="review-pending-empty-state">
              <p className="text-4xl mb-2">🎉</p>
              <p className="text-white font-medium">¡Todo valorado!</p>
              <p className="text-slate-400 text-sm mt-1">No quedan canciones sin valoración.</p>
            </div>
          ) : (
            <div className="max-h-[55vh] overflow-y-auto space-y-1 pr-1" data-testid="review-pending-list">
              {pendingReviewSongs.map((song) => (
                <div
                  key={song.id}
                  className="flex items-center justify-between gap-3 bg-slate-900/60 hover:bg-slate-900 rounded-md px-3 py-2"
                  data-testid={`review-pending-row-${song.id}`}
                >
                  <div className="min-w-0 flex-1">
                    <p className="text-white text-sm truncate">{song.title}</p>
                    <p className="text-slate-400 text-xs truncate">{song.artist}</p>
                  </div>
                  <StarRating
                    rating={0}
                    onRate={(newRating) => handleRatePendingSong(song, newRating)}
                    editable={true}
                  />
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <Button variant="outline" onClick={handleCloseReviewPending} className="border-slate-600">
              Cerrar
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
